import React, { useEffect, useState, useContext } from "react";
import {  Box, Button, IconButton, Tooltip, Typography, CircularProgress } from "@mui/material";
import "./OrganizationList.css";
import { GetOrganization, DeleteOrganization } from "../../Api/GetOrganization";
import { makeStyles } from "@mui/styles";
import Loader from "../Loader/Loader";
import StandardListView from "../StandardComponents/StandardListView/StandardListView";
import StandardFormView from "../StandardComponents/StandardFormView/StandardFormView";
import { toast } from "react-toastify";
import { FileUploadGet } from "../../Api/FileUpload";
import { useLocation, useNavigate } from "react-router-dom";
import { GetOrganizationById } from "../../Api/GetOrganizationById";
import { hasTechAdminAccess, hasConsultantAccess, isTrinityPortal } from '../../Utils/adminUserTypes';
import { getSubscriptionDurationLabel } from '../../Utils/subscriptionDuration';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import DomainIcon from '@mui/icons-material/Domain';
import AddIcon from '@mui/icons-material/Add';
import { HELPDESK_URLS } from "../../Constants/helpdeskUrls";
import OrganizationQuickFilters from './OrganizationQuickFilters';
import { SwitchAccount } from "../../Api/switchAccount";
import { AuthContext } from "../../Layout/AuthContext";
import { userListByOrganization } from "../../Api/UserListbyOrganization";
import { GetProjectData } from "../../Api/Project";
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DialogOrganizationAdd from '../StandardComponents/StandardDialog/DialogOrganizationAdd';
import useD8ListLayout from '../../hooks/useD8ListLayout';
import { toggleQuickFilter } from '../../Utils/toggleQuickFilter';

// Custom styles for hiding action buttons until hover
const useStyles = makeStyles(() => ({
  actionButtons: {
    opacity: 0,
    transition: "opacity 0.2s ease",
  },
  tableRow: {
    "&:hover $actionButtons": {
      opacity: 1,
    },
  },
  tableContainerRoot: {
    maxHeight: 450,
    overflow: "auto",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "#F1F1F2",
  },
}));

const OrganizationList = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [orgData, setOrgData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null); // For edit
  const [updatePinBarData, setUpdatePinBarData] = useState(false); // To trigger data refresh
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [flagPage, setFlagPage] = useState("");
  const [editItem, setEditItem] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const isEdit = true;
  const showMoreIcon = true;
  const showtrashIcon = false;
  const userData = JSON.parse(localStorage.getItem("userLogindata"));
  // Add current organization tracking
  const [currentOrganizationId, setCurrentOrganizationId] = useState(localStorage.getItem("organizationId") || null);
  // Access auth context for switching accounts
  const { switchAccount } = useContext(AuthContext);
  const [orgUserCounts, setOrgUserCounts] = useState({});
  const [orgProjectCounts, setOrgProjectCounts] = useState({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [metricsLoaded, setMetricsLoaded] = useState(false);
  const [isStandardDialogOpen, setIsStandardDialogOpen] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const search_params = queryParams.get('organization');
  const action_param = queryParams.get('action');
  const techAdminAccess = hasTechAdminAccess();
  const consultantAccess = hasConsultantAccess();
 
  const columns = [
    { 
      label: "Organization Name", 
      field: "organization_name",
      render: (row) => {
        // Check for different possible logo URL properties
        const logoUrl = row?.logo1_sas_url || row?.logo1_file_url || null;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {logoUrl ? (
              <Box 
                component="img"
                src={logoUrl}
                alt={`${row.organization_name} logo`}
                sx={{ 
                  width: 32, 
                  height: 32, 
                  borderRadius: '50%',
                  marginRight: 2,
                  objectFit: 'contain',
                  border: '1px solid #eee'
                }}
                onError={(e) => {
                  console.log("Error loading image:", e);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <Box 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  borderRadius: '50%',
                  marginRight: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f1f1f3',
                  color: '#7E8299',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {row.organization_name.charAt(0).toUpperCase()}
              </Box>
            )}
            <Typography variant="body2">
              {row.organization_name}
            </Typography>
          </Box>
        );
      }
    },
    { label: "Total Users", field: "total_users", 
      render: (row) => (
        <Typography variant="body2" sx={{ color: '#333' }}>
          {isLoadingCounts && orgUserCounts[row.id] === undefined ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={14} thickness={4} sx={{ color: '#1D3DFC' }} />
              <Box component="span" sx={{ color: '#84848f', fontSize: '12px', ml: 0.5 }}>Loading</Box>
            </Box>
          ) : orgUserCounts[row.id] !== undefined ? (
            orgUserCounts[row.id]
          ) : (
            <Typography variant="caption" sx={{ color: '#84848f', fontStyle: 'italic' }}>
              -
            </Typography>
          )}
        </Typography>
      )
    },
    { label: "Total Projects", field: "total_projects", 
      render: (row) => (
        <Typography variant="body2" sx={{ color: '#333' }}>
          {isLoadingCounts && orgProjectCounts[row.id] === undefined ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={14} thickness={4} sx={{ color: '#1D3DFC' }} />
              <Box component="span" sx={{ color: '#84848f', fontSize: '12px', ml: 0.5 }}>Loading</Box>
            </Box>
          ) : orgProjectCounts[row.id] !== undefined ? (
            orgProjectCounts[row.id]
          ) : (
            <Typography variant="caption" sx={{ color: '#84848f', fontStyle: 'italic' }}>
              -
            </Typography>
          )}
        </Typography>
      )
    },
    { label: "Subscription Status", field: "subscription_status", 
      render: (row) => {
        // Handle missing values and extract data with fallbacks
        const days = row.subscription_remaining_days || 0;
        const isExpired = row.subscription_status === 'expired';
        const isExpiring = days <= 14 && days > 0;
        
        // Determine subscription type label from account_expiration_day.
        // 30 is shared by Trial - 30 Days and Essentials - Monthly; prefer trial.
        const subscriptionType = getSubscriptionDurationLabel(row.account_expiration_day);
        
        // This is the exact getStatusInfo logic from StandardTabBar.js
        const getStatusInfo = () => {
          if (isExpired) {
            return {
              icon: <LockIcon fontSize="small" sx={{ color: '#F5C935' }} />,
              label: 'Expired',
              chipColor: {
                backgroundColor: '#FFF9E6',
                color: '#F5C935',
                border: '1px solid #F5C935'
              }
            };
          }
          
          if (days >= 14) {
            return {
              icon: <CheckCircleIcon fontSize="small" sx={{ color: '#67deb6' }} />,
              label: 'Active',
              chipColor: {
                backgroundColor: '#ebfef8',
                color: '#67deb6',
                border: '1px solid #ccefe3'
              }
            };
          }
          
          return {
            icon: <AccessTimeIcon fontSize="small" sx={{ color: '#84848f' }} />,
            label: `Trial ends in ${days} days`,
            chipColor: {
              backgroundColor: '#f1f1f3',
              color: '#84848f',
              border: '1px solid #dad9dd'
            }
          };
        };
        
        const statusInfo = getStatusInfo();
        
        // Exact copy of the StandardTabBar.js styling
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center' 
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              border: statusInfo.chipColor.border,
              backgroundColor: statusInfo.chipColor.backgroundColor,
              padding: '4px 8px',
              borderRadius: '8px',
              maxWidth: '180px'
            }}>
              {/* Text Content */}
              <Box sx={{ flex: 1, marginRight: '8px' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                  {subscriptionType}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: statusInfo.chipColor.color,
                      fontWeight: 'bold',
                    }}
                  >
                    {statusInfo.label}
                  </Typography>
                </Box>
              </Box>

              {/* Icon */}
              <Box sx={{ color: statusInfo.chipColor.color }}>
                {statusInfo.icon}
              </Box>
            </Box>
          </Box>
        );
      }
    },
  ];
  
  const getBaseActions = (row) => ({
    Overview: () => onActionOverview(row),
    Edit: () => onActionEdit(row),
    Delete: () => onActionDelete(row),
    'Switch Account': () => handleSwitchAccount(row)
  });

  const getActionConfig = () => {
    if (isTrinityPortal()) {
      return techAdminAccess || consultantAccess ? ['Overview', 'Edit', 'Delete'] : ['Overview', 'Edit'];
    }
    const baseActions = techAdminAccess || consultantAccess 
      ? ['Overview', 'Edit', 'Delete', 'Switch Account']
      : ['Overview', 'Edit', 'Switch Account'];
    
    return baseActions;
  };

  const actions = getActionConfig().map(actionName => ({
    label: actionName,
    onClick: (row) => getBaseActions(row)[actionName](),
    ...(actionName === 'Switch Account' && { icon: <SwitchAccountIcon sx={{ color: 'inherit' }} /> })
  }));

  // Function to set current organization
  const handleSetAsCurrent = (row) => {
    if (row && row.id) {
      localStorage.setItem("organizationId", row.id);
      setCurrentOrganizationId(row.id);
      toast.success(`${row.organization_name} set as current organization`);
      
      // Reorganize the rows to put current organization first
      sortRowsWithCurrentFirst(row.id);
      
      // Optional: Refresh the page to apply organization context
      window.location.reload();
    }
  };
  
  // Function to handle switching accounts
  const handleSwitchAccount = async (row) => {
    if (!row || !row.account_id) {
      toast.error("Account information is missing");
      return;
    }
    
    try {
      setLoading(true);
      const res = await SwitchAccount(row.account_id);
      if (res?.data) {
        // Update auth context and local storage
        switchAccount(res.data, 0, () => {}, () => {});
        localStorage.setItem("organizationId", row.id);
        toast.success(`Switched to ${row.organization_name}`);
        // Refresh page to apply changes
        window.location.reload();
      }
    } catch (error) {
      console.error("Error switching accounts:", error);
      toast.error("Unable to switch accounts");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle onboarding click
  const handleOnboardingClick = async (row) => {
    if (!row || !row.id || !row.account_id) {
      toast.error("Organization information is missing");
      return;
    }
    
    try {
      setLoading(true);
      
      // First switch account
      const res = await SwitchAccount(row.account_id);
      if (res?.data) {
        // Update auth context and local storage
        switchAccount(res.data, 0, () => {}, () => {});
        localStorage.setItem("organizationId", row.id);
        
        // Store the organization ID for onboarding
        sessionStorage.setItem("onboardingOrgId", row.id);
        
        toast.success(`Switched to ${row.organization_name} - to Complete Onboarding Details`);
        
        // Navigate to onboarding page
        navigate("/onboarding");
      } else {
        throw new Error("Failed to switch account");
      }
    } catch (error) {
      console.error("Error switching account for onboarding:", error);
      toast.error("Failed to switch account. Cannot access onboarding.");
    } finally {
      setLoading(false);
    }
  };

  // Handle simplified business details click (/bizdetails)
  const handleBizDetailsClick = async (row) => {
    if (!row || !row.id || !row.account_id) {
      toast.error("Organization information is missing");
      return;
    }
    try {
      setLoading(true);
      const res = await SwitchAccount(row.account_id);
      if (res?.data) {
        switchAccount(res.data, 0, () => {}, () => {});
        localStorage.setItem("organizationId", row.id);
        // Optional context for simplified onboarding flow
        sessionStorage.setItem("onboardingOrgId", row.id);
        toast.success(`Switched to ${row.organization_name} - to Business Details`);
        navigate("/bizdetails");
      } else {
        throw new Error("Failed to switch account");
      }
    } catch (error) {
      console.error("Error switching account for business details:", error);
      toast.error("Failed to switch account. Cannot access business details.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle projects click - modified to open modal directly
  const handleProjectsClick = (row) => {
    if (!row || !row.id) {
      toast.error("Organization information is missing");
      return;
    }
    
    // Set selected organization for the modal
    setSelectedOrgForModal(row);
    
    // Open projects modal
    setIsProjectsModalOpen(true);
  };
  
  // Handle add users click - modified to open modal directly
  const handleAddUsersClick = (row) => {
    if (!row || !row.id) {
      toast.error("Organization information is missing");
      return;
    }
    
    // Set selected organization for the modal
    setSelectedOrgForModal(row);
    
    // Open users modal
    setIsUsersModalOpen(true);
  };
  
  // Close modals handler
  const handleCloseModals = () => {
    setIsUsersModalOpen(false);
    setIsProjectsModalOpen(false);
    setSelectedOrgForModal(null);
  };
  
  // Function to sort rows with current organization first
  const sortRowsWithCurrentFirst = (orgId) => {
    if (!orgId || !Array.isArray(rows) || rows.length <= 1) return;
    
    const sortedRows = [...rows].sort((a, b) => {
      if (a.id === orgId) return -1;
      if (b.id === orgId) return 1;
      return 0;
    });
    
    setRows(sortedRows);
  };
    
  const handleOnclose = () => {
    setIsModalOpen(!isModalOpen);
  };
  const onActionOverview = (row) => {
    setFlagPage("view");
    setEditItem(row);
    setIsModalOpen(!isModalOpen);
  };
  const onActionEdit = (row) => {
    setEditItem(row);
    setFlagPage("edit");
    if (!isModalOpen) setIsModalOpen(true);
  };
  const onActionDelete = async (row) => {
    try {
      setLoading(true);
      const response = await DeleteOrganization(row?.id);
      if ( response) {
        toast.success("Organization deleted successfully");
        fetchOrgData(); 
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => {
    if(search_params){
      GetOrgAllDataById();
    }else{
      fetchOrgData();
    }
    
    // Check if there's an action=create parameter to open the Create Organization modal
    if(action_param === 'create' && (techAdminAccess || consultantAccess)) {
      setFlagPage("add");
      setIsModalOpen(true);
    }
  }, []);

  // Function to fetch user and project counts for each organization (LAZY LOADED - only when user clicks "Load Metrics")
  const fetchOrgMetrics = async (organizations) => {
    if (!Array.isArray(organizations) || organizations.length === 0) return;
    if (metricsLoaded) return; // Don't reload if already fetched
    
    setIsLoadingCounts(true);
    const userCountsObj = {};
    const projectCountsObj = {};
    
    try {
      // Process organizations in parallel batches
      const processBatch = async (batch) => {
        const userPromises = batch.map(org => 
          userListByOrganization(org.id)
            .then(response => {
              if (response?.data && Array.isArray(response.data)) {
                userCountsObj[org.id] = response.data.length;
              } else {
                userCountsObj[org.id] = 0;
              }
            })
            .catch(() => {
              userCountsObj[org.id] = 0;
            })
        );
        
        const projectPromises = batch.map(org => 
          GetProjectData(org.id)
            .then(response => {
              if (response?.data && Array.isArray(response.data)) {
                projectCountsObj[org.id] = response.data.length;
              } else {
                projectCountsObj[org.id] = 0;
              }
            })
            .catch(() => {
              projectCountsObj[org.id] = 0;
            })
        );
        
        await Promise.all([...userPromises, ...projectPromises]);
      };
      
      // Process in batches of 3 to prevent too many concurrent requests
      const batchSize = 3;
      for (let i = 0; i < organizations.length; i += batchSize) {
        const batch = organizations.slice(i, i + batchSize);
        await processBatch(batch);
        
        // Update state after each batch for better UX
        setOrgUserCounts({...userCountsObj});
        setOrgProjectCounts({...projectCountsObj});
      }
    } catch (error) {
      console.error("Error fetching organization metrics:", error);
    } finally {
      setIsLoadingCounts(false);
      setMetricsLoaded(true);
    }
  };
  
  // Function to enhance organization row data with logo URLs
  const enhanceOrganizationsWithLogos = async (organizations) => {
    if (!Array.isArray(organizations) || organizations.length === 0) return organizations;
    
    // Map through each organization and fetch logo data if needed
    const enhancedOrgs = await Promise.all(organizations.map(async (org) => {
      // Skip if org already has logo URL
      if (org.logo1_sas_url || org.logo1_file_url) {
        return org;
      }
      
      // If org has logo1 ID but no URL, try to fetch it
      if (org.logo1) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log("Fetching logo for org:", org.organization_name, "ID:", org.logo1);
          }
          const response = await FileUploadGet(org.logo1);
          
          if (response?.data?.data?.sas_url) {
            return { 
              ...org, 
              logo1_sas_url: response.data.data.sas_url 
            };
          }
        } catch (error) {
          console.error("Error fetching logo for org:", org.organization_name, error);
        }
      }
      
      return org;
    }));
    
    return enhancedOrgs;
  };
 
  const fetchOrgData = async () => {
    try {
      setLoading(true);
      const response = await GetOrganization(userData?.ID || userData?.user?.user_id);
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Organization API response:", response);
        
        // Log the first organization to see its structure
        if (response?.data && response.data.length > 0) {
          const sampleOrg = response.data[0];
          console.log("Sample organization object:", sampleOrg);
          console.log("Subscription fields:", {
            subscription_status: sampleOrg.subscription_status,
            subscription_remaining_days: sampleOrg.subscription_remaining_days,
            account_expiration_day: sampleOrg.account_expiration_day,
            subscription_type: sampleOrg.subscription_type
          });
        }
      }
      
      setOrgData(response?.data);
      let rowsData = response.status === 200 ? response.data : [];
      
      if (Array.isArray(rowsData)) {
        // Enhance organizations with logo URLs if needed
        rowsData = await enhanceOrganizationsWithLogos(rowsData);
        
        // Sort rows to put current organization first
        if (currentOrganizationId) {
          const sortedRows = rowsData.sort((a, b) => {
            if (a.id === currentOrganizationId) return -1;
            if (b.id === currentOrganizationId) return 1;
            return 0;
          });
          setRows(sortedRows);
          
          // DON'T auto-fetch metrics on page load - let user load them on demand
          // fetchOrgMetrics(sortedRows);
        } else {
          setRows(rowsData);
          
          // DON'T auto-fetch metrics on page load - let user load them on demand
          // fetchOrgMetrics(rowsData);
        }
      } else {
        console.error("Invalid data format received from API");
      }
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const GetOrgAllDataById = async () => {
    try {
        setLoading(true);
        const response = await GetOrganizationById(search_params);
        if (process.env.NODE_ENV === 'development') {
          console.log('response', response);
        }
        setRows([response?.data]);
        setIsModalOpen(true)
        setEditItem(response?.data);
        setFlagPage("edit");
        
        // DON'T auto-fetch metrics - let user load them on demand
        // if (response?.data) {
        //   fetchOrgMetrics([response.data]);
        // }
      } catch (error) {
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
  };

  const onActionAdd = (row) => {
    setFlagPage("add");
    setIsModalOpen(!isModalOpen);
  };

  // Handlers for standard dialog
  const handleOpenStandardDialog = () => {
    setIsStandardDialogOpen(true);
  };

  const handleCloseStandardDialog = () => {
    setIsStandardDialogOpen(false);
    // Refresh organization data after creating new organization
    fetchOrgData();
  };

  const [editDataStore, setEditDataStore] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const { pageShellSx, listFlexSx, listViewProps } = useD8ListLayout();

  // Add visual indicator for current organization
  const getRowStyle = (row) => {
    return row.id === currentOrganizationId ? {
      backgroundColor: 'rgba(29, 61, 252, 0.05)',
      borderLeft: '3px solid #1D3DFC'
    } : {};
  };

  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [isProjectsModalOpen, setIsProjectsModalOpen] = useState(false);
  const [selectedOrgForModal, setSelectedOrgForModal] = useState(null);

  return (
    <Box sx={pageShellSx}>
      <Loader message="" isVisible={loading} />
      <style>{`
        .action-buttons-container {
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
        }
        .MuiTableRow-root:hover .action-buttons-container {
          opacity: 1;
        }
      `}</style>
      {/* Load Metrics Button */}
      {!metricsLoaded && rows.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => fetchOrgMetrics(rows)}
            disabled={isLoadingCounts}
            sx={{ textTransform: 'none' }}
          >
            {isLoadingCounts ? 'Loading Metrics...' : 'Load User & Project Counts'}
          </Button>
        </Box>
      )}
      
      <Box sx={listFlexSx}>
      <StandardListView
        {...listViewProps}
        columns={columns}
        rows={rows}
        isEdit={isEdit}
        actions={actions}
        handleOnclose={handleOnclose}
        flag={flagPage}
        addButtonLabel={(techAdminAccess || consultantAccess) ? "Add" : ""}
        onAddButtonClick={onActionAdd}
        showMoreIcon={showMoreIcon}
        title={"Organization"}
        helpdeskUrl={HELPDESK_URLS.ORGANIZATION}
        enableQuickFilter={false}
        quickFilterComponent={
          <OrganizationQuickFilters
            rows={rows}
            activeFilters={activeFilters}
            onApplyFilter={(filter) => setActiveFilters((prev) => toggleQuickFilter(prev, filter))}
          />
        }
        initialActiveFilters={activeFilters}
        onFilterChange={(newFilters) => setActiveFilters(newFilters)}
        customHeaderButtons={
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenStandardDialog}
            sx={{
              borderColor: '#1D3DFC',
              color: '#1D3DFC',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#0E2BC0',
                backgroundColor: 'rgba(29, 61, 252, 0.04)'
              }
            }}
          >
            Create Organization
          </Button>
        }
        getRowStyle={getRowStyle}
        customActionButton={(row) => (
          <Box 
            className="action-buttons-container"
            sx={{ 
              display: 'flex', 
              gap: 1,
              width: '170px',  // Increased for 4 icons
              justifyContent: 'flex-end', // Right-align buttons
              marginRight: '12px', // Margin from More Actions icon
              marginLeft: '12px', // Left margin
              position: 'relative',
            }}
          >
            {!isTrinityPortal() && (
            <>
            {/* Business Details shortcut */}
            <Tooltip title="Complete Business Details (Onboard)">
              <IconButton
                onClick={() => handleBizDetailsClick(row)}
                sx={{
                  color: '#7E8299',
                  '&:hover': {
                    backgroundColor: 'rgba(126, 130, 153, 0.04)'
                  }
                }}
              >
                <DomainIcon />
              </IconButton>
            </Tooltip>

            {/* Onboarding shortcut with new icon */}
            <Tooltip title="Complete Onboarding Details">
              <IconButton
                onClick={() => handleOnboardingClick(row)}
                sx={{
                  color: '#7E8299',
                  '&:hover': {
                    backgroundColor: 'rgba(126, 130, 153, 0.04)'
                  }
                }}
              >
                <BusinessCenterIcon />
              </IconButton>
            </Tooltip>
            </>
            )}
            
            {/* Projects shortcut */}
            <Tooltip title="Add Projects">
              <IconButton
                onClick={() => handleProjectsClick(row)}
                sx={{
                  color: '#7E8299',
                  '&:hover': {
                    backgroundColor: 'rgba(126, 130, 153, 0.04)'
                  }
                }}
              >
                <img 
                  src="/assets/icons/menu-project-icon.png" 
                  alt="Add Projects" 
                  style={{ width: '24px', height: '24px' }} 
                />
              </IconButton>
            </Tooltip>
            
            {/* Add Users shortcut */}
            <Tooltip title="Add Users">
              <IconButton
                onClick={() => handleAddUsersClick(row)}
                sx={{
                  color: '#7E8299',
                  '&:hover': {
                    backgroundColor: 'rgba(126, 130, 153, 0.04)'
                  }
                }}
              >
                <PersonAddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />
      </Box>
      {isModalOpen && (
        <StandardFormView
          open={isModalOpen}
          isPage={"organization"}
          flag={flagPage}
          onClose={handleOnclose}
          actions={actions}
          title={
            flagPage === "add"
              ? "Create Organization"
              : flagPage === "edit"
              ? "Edit Organization"
              : "Overview Organization"
          }
          subTitle={"Organization Details"}
          getAllData={fetchOrgData}
          editItems={editItem}
          onEdit={() => onActionEdit(editItem)}
        />
      )}
      
      {/* Project Modal */}
      {isProjectsModalOpen && (
        <StandardFormView
          open={isProjectsModalOpen}
          isPage={"projects"}
          flag={"add"}
          onClose={handleCloseModals}
          title={"Create Project"}
          subTitle={"Project Details"}
          getAllData={() => fetchOrgMetrics([selectedOrgForModal])}
          editItems={{
            organization_id: selectedOrgForModal?.id,
            organization_name: selectedOrgForModal?.organization_name
          }}
        />
      )}
      
      {/* Users Modal */}
      {isUsersModalOpen && (
        <StandardFormView
          open={isUsersModalOpen}
          isPage={"users"}
          flag={"add"}
          onClose={handleCloseModals}
          title={"Create User"}
          subTitle={"User Details"}
          getAllData={() => fetchOrgMetrics([selectedOrgForModal])}
          editItems={{
            organization_id: selectedOrgForModal?.id,
            organization_name: selectedOrgForModal?.organization_name
          }}
        />
      )}
      
      {/* Standard Dialog for Adding Organization */}
      <DialogOrganizationAdd 
        open={isStandardDialogOpen}
        onClose={handleCloseStandardDialog}
        context="organization_list"
      />
    </Box>
  );
};
export default OrganizationList;